import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Divider, Button } from '@mui/material';
import downloadIcon from '/src/assets/images/button/download.svg?url';

import DeactivateAppModal from './ModalPublish/DeactivateAppModal';
import UnpublishAppModal from './ModalPublish/UnpublishAppModal';
import ChangeLogModal from './ModalPublish/ChangeLogModal';
import DownloadModal from './ModalPublish/DownloadModal';

interface AppDetailsProps {
  version: any;
  releaseDate: string;
  key: string;
  publisherId: string;
  appId: string;
  active: boolean;
  /** True once sibche has approved and published the listing. */
  canManagePublish?: boolean;
}

const AppVersionBox: React.FC<AppDetailsProps> = ({
  version,
  releaseDate,

  publisherId,
  appId,
  active: initialActive,
  canManagePublish = false,
}) => {
  const {
    version_number,

    change_log,

    version_text,
    status,
    download_url,
    published_at_text: publishedAt,
  } = version || {};
  const [openlog, setOpenlog] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [active, setActive] = useState(initialActive);

  useEffect(() => {
    setActive(initialActive);
  }, [initialActive]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleConfirmDeactivation = (nextActive: boolean) => {
    setModalOpen(false);
    setActive(nextActive);
  };

  const handleDownloadClick = () => {
    setDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setDownloadModalOpen(false);
  };

  return (
    <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            lineHeight: '20px',
            wordWrap: 'break-word',
            color: '#60646E',
          }}
        >
          {version_number}شماره نسخه
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            lineHeight: '20px',
            wordWrap: 'break-word',
            color: '#25282E',
          }}
        >
          {version_text}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        {/*
          Two distinct dates: when the publisher uploaded the build, and when
          it last went live. The second is null until sibche publishes it, and
          moves again on every republication — the label used to read
          "تاریخ انتشار" over the upload date, which never changed.
        */}
        <Stack direction="column" sx={{ width: '100%' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 400,
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 12,
              lineHeight: '20px',
              color: '#60646E',
            }}
          >
            افزوده شده در {releaseDate}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 400,
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 12,
              lineHeight: '20px',
              color: '#60646E',
            }}
          >
            تاریخ انتشار {publishedAt || '—'}
          </Typography>
        </Stack>
        <Typography
          color="textPrimary"
          sx={{
            fontWeight: 600,
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
            color: '#25282E',
          }}
        >
          {status}
        </Typography>
      </Stack>
      {/*
        A two column grid rather than a flex row: every button gets the same
        width whatever its label, so a set of two and a set of four both stay
        aligned. Fixed widths in a flex row squeezed the persian labels onto
        two lines and left the buttons visibly uneven.
      */}
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '6px',
          direction: 'rtl',
          paddingTop: '6px',
          paddingBottom: '6px',
          marginTop: '16px',
        }}
      >
        {/*
          Both controls only make sense on a listing sibche has approved and
          published: before that there is nothing to hide or withdraw, and
          pressing either would have no visible effect.
        */}
        {canManagePublish && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              height: '25px',
              borderRadius: 0.5,
              boxShadow: 0,
              border: 1,
              borderColor: active ? '#EF4056' : '#A6AAB5',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 12,
              lineHeight: '20px',
              wordWrap: 'break-word',
              padding: 2,
              backgroundColor: active ? '#FFD6DB' : '#F2F2F5',
              // The theme paints every `contained` button white-on-blue, which
              // is unreadable on these pale fills.
              color: active ? '#EF4056' : '#60646E',
              transition: 'background-color .2s ease, color .2s ease',
              '&:hover': {
                backgroundColor: active ? '#EF4056' : '#60646E',
                borderColor: active ? '#EF4056' : '#60646E',
                color: '#FFFFFF',
              },
            }}
          >
            {active ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
          </Button>
        )}

        {canManagePublish && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setUnpublishModalOpen(true)}
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              height: '25px',
              borderRadius: 0.5,
              boxShadow: 0,
              border: 1,
              borderColor: '#EF4056',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 12,
              lineHeight: '20px',
              wordWrap: 'break-word',
              padding: 2,
              backgroundColor: '#FFF0F1',
              color: '#EF4056',
              transition: 'background-color .2s ease, color .2s ease',
              '&:hover': {
                backgroundColor: '#EF4056',
                borderColor: '#EF4056',
                color: '#FFFFFF',
              },
            }}
          >
            برداشتن از فروشگاه
          </Button>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadClick}
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
            height: '25px',
            borderRadius: 0.5,
            boxShadow: 0,
            border: 1,
            backgroundColor: '#F2F2F5',
            borderColor: '#A6AAB5',
            color: '#60646E',
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            lineHeight: '20px',
            wordWrap: 'break-word',
            padding: 2, // پدینگ یکسان
            display: 'flex', // برای ترازبندی بهتر آیکون و متن
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color .2s ease, color .2s ease',
            // The fill darkens with the text, otherwise white-on-#F2F2F5
            // would be unreadable. The icon is an <img>, so it needs the
            // filter rather than `color`.
            '&:hover': {
              backgroundColor: '#60646E',
              borderColor: '#60646E',
              color: '#FFFFFF',
              '& img': { filter: 'brightness(0) invert(1)' },
            },
          }}
        >
          <img
            src={downloadIcon}
            alt="icon"
            style={{ width: '20px', height: '20px', marginLeft: '4px' }}
          />
          دانلود نسخه
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenlog(true)}
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
            height: '25px',
            borderRadius: 0.5,
            boxShadow: 0,
            border: 1,
            backgroundColor: '#F2F2F5',
            borderColor: '#A6AAB5',
            color: '#60646E',
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            lineHeight: '20px',
            wordWrap: 'break-word',
            padding: 2, // پدینگ یکسان
            transition: 'background-color .2s ease, color .2s ease',
            '&:hover': {
              backgroundColor: '#60646E',
              borderColor: '#60646E',
              color: '#FFFFFF',
            },
          }}
        >
          تغییرات نسخه
        </Button>
      </Box>
      <DeactivateAppModal
        open={modalOpen}
        onClose={handleCloseModal}
        publisherId={publisherId}
        appId={appId}
        active={active}
        onConfirm={handleConfirmDeactivation}
      />

      <UnpublishAppModal
        open={unpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        publisherId={publisherId}
        appId={appId}
        onConfirm={() => setUnpublishModalOpen(false)}
      />

      <ChangeLogModal
        open={openlog}
        onClose={() => setOpenlog(false)}
        changeLog={change_log}
      />

      <DownloadModal
        open={downloadModalOpen}
        onClose={handleCloseDownloadModal}
        downloadUrl={download_url}
      />

      <Divider sx={{ width: '100%', margin: '0px 0' }} />
    </Stack>
  );
};

export default AppVersionBox;
